import ActionButton from '@atoms/button';
import { GlassModal } from '@atoms/glassModal';
import { SelectField } from '@atoms/select';
import { Column } from '@components/dnd/base/blockBase/body.block.style';
import { MonacoEditor } from '@components/monaco/monacoEditor.code';
import { STATIC_TYPES } from '@models/code/starter.types';
import type { OnChange } from '@monaco-editor/react';
import {
  FormControl,
  FormLabel,
  ListItemDecorator,
  Option,
  type SelectOption
} from '@mui/joy';
import {
  type GlobalVariableData,
  PRIMARY_TYPES
} from '@stratumn/studio-shared';
import { WandSparkles } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { newError } from '@/services/errors/errors';

import {
  getCustomTypeName,
  isCorrectPrimaryType,
  primaryTypeToInfoMap
} from './globalVariable.utils';

type Props = GlobalVariableData['variableType'] & {
  onTypeChange: (type: GlobalVariableData['variableType']['type']) => void;
  onCustomTypeChange: (
    customType: GlobalVariableData['variableType']['customType']
  ) => void;
};
export const TypeSelector = ({
  selectedKind,
  type,
  customType,
  onTypeChange,
  onCustomTypeChange
}: Props) => {
  const [customTypeHasError, setCustomTypeHasError] = useState<boolean>(true);
  const [isCustomTypeModelOpen, setIsCustomTypeModelOpen] = useState(false);
  const checkHasErrorCustomType = useCallback((): boolean => {
    if (selectedKind !== 'custom') {
      return false;
    }
    if (!customType) {
      return true;
    }
    const hasNamedType = !!getCustomTypeName(customType);
    const isEndCorrect = customType.endsWith(';');
    return !(hasNamedType && isEndCorrect);
  }, [customType, selectedKind]);

  useEffect(() => {
    setCustomTypeHasError(() => checkHasErrorCustomType());
  }, [checkHasErrorCustomType]);

  const onPrimaryTypeChange = (value: unknown): void => {
    if (!isCorrectPrimaryType(value)) {
      newError('PRIM-1000', 'Invalid primary type', true);
      return;
    }
    if (selectedKind === 'custom') {
      newError(
        'PRIM-2000',
        'Cannot select a primary type for custom type',
        true
      );
      return;
    }
    onTypeChange(value);
  };

  const onCodeChange: OnChange = code => {
    if (!code) {
      return;
    }
    onCustomTypeChange(code);
  };

  if (selectedKind === 'custom') {
    return (
      <Column $width="185px">
        <FormControl error={customTypeHasError} required size="sm">
          <FormLabel
            style={{
              color: customTypeHasError ? '#c41c1c' : undefined
            }}
          >
            Custom type
          </FormLabel>
          <ActionButton
            color="neutral"
            onClick={() => setIsCustomTypeModelOpen(true)}
            startDecorator={<WandSparkles size={18} />}
            value="Edit custom type"
            variant="soft"
          />
        </FormControl>
        <GlassModal
          className="h-full w-full px-4"
          header={{
            title: 'Custom type editor',
            description: 'Customize the type of your variable'
          }}
          onClose={() => setIsCustomTypeModelOpen(false)}
          open={isCustomTypeModelOpen}
        >
          <section className="-ml-4 h-full w-full overflow-hidden">
            <MonacoEditor
              code={customType || DEFAULT_CODE}
              onCodeChange={onCodeChange}
              options={{
                hover: {
                  enabled: true
                },
                scrollbar: {
                  useShadows: false,
                  vertical: 'auto'
                },
                scrollBeyondLastLine: false,
                padding: {},
                lineDecorationsWidth: 3,
                lineNumbersMinChars: 3,
                contextmenu: false,
                overviewRulerBorder: false,
                overviewRulerLanes: 0
              }}
              preCode={STATIC_TYPES + PRE_TYPE_SECURITY}
            />
          </section>
        </GlassModal>
      </Column>
    );
  }
  return (
    <Column $width="200px">
      <FormControl size="sm">
        <SelectField
          label="Primary type"
          onChange={(_, type) => onPrimaryTypeChange(type)}
          renderValue={renderSelectedPrimaryTypeOption}
          required
          sx={{
            '--ListItemDecorator-size': '25px',
            width: '100%'
          }}
          value={type}
        >
          {PRIMARY_TYPES.map(primaryType => {
            const { label, icon: Icon } = primaryTypeToInfoMap[primaryType];
            return (
              <Option key={primaryType} label={label} value={primaryType}>
                <ListItemDecorator>
                  <Icon size={14} />
                </ListItemDecorator>
                {label}
              </Option>
            );
          })}
        </SelectField>
      </FormControl>
    </Column>
  );
};

const renderSelectedPrimaryTypeOption = (
  option: SelectOption<string> | null
): ReactNode => {
  if (!option) {
    return null;
  }

  const { label, icon: Icon } =
    primaryTypeToInfoMap[option.value as (typeof PRIMARY_TYPES)[number]];
  return (
    <>
      <ListItemDecorator>
        <Icon size={14} />
      </ListItemDecorator>
      {label}
    </>
  );
};

const DEFAULT_TYPENAME = 'MyType';
const DEFAULT_CODE = `type ${DEFAULT_TYPENAME} = ;` as const;

const PRE_TYPE_SECURITY =
  `/** Please replace \`${DEFAULT_TYPENAME}\` with a custom type name. */
type ${DEFAULT_TYPENAME} = never;` as const;
